/**
 * API functions for attendee profile management.
 * Profile read/write goes through the profile Edge Function.
 */
import { supabase } from '../supabase';
import { normalizeSupabaseError } from './errors';
import type {
  AttendeeProfile,
  AttendeeProfileWithDetails,
  EmergencyContact,
  ChurchInfo,
  PersonalReference,
  FaithInfo,
  ProfileCustomField,
} from '../../types/database';

async function protectedProfileGet(): Promise<AttendeeProfileWithDetails | null> {
  const { data, error } = await supabase.functions.invoke('profile', { method: 'GET' });
  if (error) throw normalizeSupabaseError(error);
  if (data?.error) throw new Error(data.error);
  return data?.data ?? null;
}

async function protectedProfilePost(body: {
  profile?: Partial<AttendeeProfile>;
  emergency_contact?: Partial<EmergencyContact>;
  church_info?: Partial<ChurchInfo>;
  faith_info?: Partial<FaithInfo>;
  personal_references?: Partial<PersonalReference>[];
}): Promise<AttendeeProfileWithDetails> {
  const { data, error } = await supabase.functions.invoke('profile', { method: 'POST', body });
  if (error) {
    // Try to get the error message from the response context if available
    const context = (error as any)?.context;
    if (context && typeof context.json === 'function') {
      try {
        const body = await context.json();
        if (body?.error) throw new Error(body.error);
      } catch {
        // Fall through to normalizeSupabaseError
      }
    }
    throw normalizeSupabaseError(error);
  }
  if (data?.error) throw new Error(data.error);
  if (!data?.data) throw new Error('No data returned');
  return data.data as AttendeeProfileWithDetails;
}

export async function getAttendeeProfile(_userId: string): Promise<AttendeeProfileWithDetails | null> {
  return protectedProfileGet();
}

export async function createOrUpdateAttendeeProfile(
  _userId: string,
  profile: Partial<AttendeeProfile>
): Promise<AttendeeProfile> {
  const full = await protectedProfilePost({ profile });
  return full as unknown as AttendeeProfile;
}

export async function createOrUpdateEmergencyContact(
  _profileId: string,
  contact: Partial<EmergencyContact>
): Promise<EmergencyContact> {
  const full = await protectedProfilePost({ emergency_contact: contact });
  if (!full.emergency_contact) throw new Error('Emergency contact not returned');
  return full.emergency_contact as EmergencyContact;
}

export async function createOrUpdateChurchInfo(
  _profileId: string,
  church: Partial<ChurchInfo>
): Promise<ChurchInfo> {
  const full = await protectedProfilePost({ church_info: church });
  if (!full.church_info) throw new Error('Church info not returned');
  return full.church_info as ChurchInfo;
}

export async function createOrUpdateReference(
  _profileId: string,
  referenceNumber: 1 | 2,
  reference: Partial<PersonalReference>
): Promise<PersonalReference> {
  const full = await protectedProfilePost({
    personal_references: [{ ...reference, reference_number: referenceNumber }],
  });
  const refs = full.personal_references as PersonalReference[] | undefined;
  const r = refs?.find((x) => x.reference_number === referenceNumber);
  if (!r) throw new Error('Reference not returned');
  return r;
}

export async function createOrUpdateFaithInfo(
  _profileId: string,
  faith: Partial<FaithInfo>
): Promise<FaithInfo> {
  const full = await protectedProfilePost({ faith_info: faith });
  if (!full.faith_info) throw new Error('Faith info not returned');
  return full.faith_info as FaithInfo;
}

export async function markProfileAsCompleted(profileId: string): Promise<void> {
  const { error } = await supabase
    .from('attendee_profiles')
    .update({ profile_completed: true })
    .eq('id', profileId);

  if (error) throw normalizeSupabaseError(error);
}

// ============================================
// Profile Custom Fields (for Easy Apply)
// ============================================

/**
 * Get all saved custom field responses for a profile
 */
export async function getProfileCustomFields(profileId: string): Promise<ProfileCustomField[]> {
  const { data, error } = await supabase
    .from('profile_custom_fields')
    .select('*')
    .eq('attendee_profile_id', profileId);

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

/**
 * Get a specific custom field response by key
 */
export async function getProfileCustomField(
  profileId: string,
  fieldKey: string
): Promise<ProfileCustomField | null> {
  const { data, error } = await supabase
    .from('profile_custom_fields')
    .select('*')
    .eq('attendee_profile_id', profileId)
    .eq('field_key', fieldKey)
    .maybeSingle();

  if (error) throw normalizeSupabaseError(error);
  return data ?? null;
}

/**
 * Save or update a custom field response to the user's profile
 * Uses upsert to handle both insert and update cases
 */
export async function saveProfileCustomField(
  profileId: string,
  fieldKey: string,
  fieldValue: any
): Promise<ProfileCustomField> {
  const { data, error } = await supabase
    .from('profile_custom_fields')
    .upsert(
      {
        attendee_profile_id: profileId,
        field_key: fieldKey,
        field_value: fieldValue,
      },
      {
        onConflict: 'attendee_profile_id,field_key',
      }
    )
    .select()
    .single();

  if (error) throw normalizeSupabaseError(error);
  return data;
}

/**
 * Delete a custom field response from the user's profile
 */
export async function deleteProfileCustomField(
  profileId: string,
  fieldKey: string
): Promise<void> {
  const { error } = await supabase
    .from('profile_custom_fields')
    .delete()
    .eq('attendee_profile_id', profileId)
    .eq('field_key', fieldKey);

  if (error) throw normalizeSupabaseError(error);
}

/**
 * Batch save multiple custom field responses
 * Useful when saving all custom fields from an application at once
 */
export async function saveProfileCustomFields(
  profileId: string,
  fields: Array<{ fieldKey: string; fieldValue: any }>
): Promise<void> {
  const records = fields.map(({ fieldKey, fieldValue }) => ({
    attendee_profile_id: profileId,
    field_key: fieldKey,
    field_value: fieldValue,
  }));

  const { error } = await supabase
    .from('profile_custom_fields')
    .upsert(records, {
      onConflict: 'attendee_profile_id,field_key',
    });

  if (error) throw normalizeSupabaseError(error);
}
